import React, { Component } from 'react';
import {
  msalApp,
  requiresInteraction,
  fetchMsGraph,
  fetchMsGraphProfilePic,
  isIE,
  GRAPH_ENDPOINTS,
  GRAPH_SCOPES,
  GRAPH_REQUESTS,
} from './auth-utils';
import { Settings, UATConstants } from './Common/Models/UATConstants';
import { acquireToken, onTokenRenewal } from './Token';

// If you support IE, our recommendation is that you sign-in using Redirect APIs
const useRedirectFlow = isIE();
// const useRedirectFlow = true;

export default (C) =>
  class AuthProvider extends Component {
    constructor(props) {
      super(props);

      this.state = {
        account: null,
        error: null,
        emailMessages: null,
        graphProfile: null,
        profilePic: null,
        graphAccessToken: null,
        apiAccessToken: null,
        coreApiAccessToken: null,
      };

      const self = this;

      setInterval(function () {
        const expiryTime =
          Date.parse(sessionStorage.getItem(UATConstants.loginTokenExpiryTime)) - Date.now();
        const coreExpiryTime =
          Date.parse(sessionStorage.getItem(UATConstants.coreTokenExpiryTime)) - Date.now();
        const graphTokenExpiryTime =
          Date.parse(sessionStorage.getItem(UATConstants.graphTokenExpiryTime)) - Date.now();

        if ((expiryTime > 0 && expiryTime < Settings.PREFETCH_TOKEN_TIME) || expiryTime <= 0) {
          onTokenRenewal(UATConstants.PlaceholdersAndTitles.Api);
        }

        if (
          (coreExpiryTime > 0 && coreExpiryTime < Settings.PREFETCH_TOKEN_TIME) ||
          coreExpiryTime <= 0
        ) {
          onTokenRenewal(UATConstants.PlaceholdersAndTitles.Core);
        }

        if (
          (graphTokenExpiryTime > 0 && graphTokenExpiryTime < Settings.PREFETCH_TOKEN_TIME) ||
          graphTokenExpiryTime <= 0
        ) {
          onTokenRenewal(UATConstants.PlaceholdersAndTitles.GraPermiph);
        }
      }, Settings.CHECK_TOKEN_INTERVAL);
    }

    async onSignIn(redirect) {
      const redirectResponse = await msalApp.handleRedirectPromise({
        navigateToLoginRequestUrl: true,
      });

      let user = null;

      if (redirectResponse !== null) {
        user = redirectResponse.account;
      } else {
        const activeusers = await msalApp.getAllAccounts();
        user = !!activeusers && activeusers.length !== 0 ? activeusers[0] : null;
      }

      if (user === null) {
        return await msalApp.loginRedirect(GRAPH_REQUESTS.LOGIN);
      } else {
        await msalApp.setActiveAccount(user);

        this.setState({
          account: user,
          error: null,
        });

        const tokenResponse = await acquireToken(GRAPH_REQUESTS.LOGIN, isIE()).catch((error) => {
          this.setState({
            error: error.message,
          });
        });

        if (tokenResponse) {
          this.setState({
            graphAccessToken: tokenResponse.accessToken,
          });

          sessionStorage.setItem(UATConstants.jwtTokenForGraph, tokenResponse.accessToken);
          sessionStorage.setItem(UATConstants.graphTokenExpiryTime, tokenResponse.expiresOn);

          const graphProfile = await fetchMsGraph(
            GRAPH_ENDPOINTS.ME,
            tokenResponse.accessToken
          ).catch(() => {
            this.setState({
              error: 'Unable to fetch Graph profile.',
            });
          });

          if (graphProfile) {
            this.setState({
              graphProfile,
            });
          }

          if (tokenResponse.scopes && tokenResponse.scopes.indexOf(GRAPH_SCOPES.MAIL_READ) > 0) {
            return this.readMail(tokenResponse.accessToken);
          }
        }
      }
    }

    async onSignOut() {
      await msalApp.logoutRedirect({
        account: msalApp.getActiveAccount() ?? undefined,
      });
    }

    async onRequestEmailToken() {
      const tokenResponse = await acquireToken(GRAPH_REQUESTS.EMAIL, useRedirectFlow).catch((e) => {
        this.setState({
          error: 'Unable to acquire access token for reading email.',
        });
      });

      if (tokenResponse) {
        return this.readMail(tokenResponse.accessToken);
      }
    }

    async onRequestProfilePic() {
      const tokenResponse = await acquireToken(GRAPH_REQUESTS.LOGIN, useRedirectFlow).catch((e) => {
        this.setState({
          error: 'Unable to acquire access token for profile photo.',
        });
      });

      if (tokenResponse) {
        return this.getProfilePhoto(tokenResponse.accessToken);
      }
    }

    async getCoreAPIAccessToken() {
      const tokenResponse = await acquireToken(GRAPH_REQUESTS.CoreAPI, true).catch((e) => {
        this.setState({
          error: 'Unable to acquire access token for API.',
        });
      });

      if (tokenResponse) {
        sessionStorage.setItem(UATConstants.jwtTokenForCoreApi, tokenResponse.accessToken);
        sessionStorage.setItem(UATConstants.coreTokenExpiryTime, tokenResponse.expiresOn);
        this.setState({
          coreApiAccessToken: tokenResponse.accessToken,
        });
      }
    }

    async getAPIAccessToken() {
      const tokenResponse = await acquireToken(GRAPH_REQUESTS.API, useRedirectFlow).catch((e) => {
        this.setState({
          error: 'Unable to acquire access token for API.',
        });
      });

      if (tokenResponse) {
        this.setState({
          apiAccessToken: tokenResponse.accessToken,
        });
        sessionStorage.setItem(UATConstants.loginTokenExpiryTime, tokenResponse.expiresOn);
        sessionStorage.setItem(UATConstants.jwtTokenForApi, tokenResponse.accessToken);
      }
    }

    async getDevopsAccessToken() {
      const tokenResponse = await acquireToken(GRAPH_REQUESTS.DevopsAPI, useRedirectFlow).catch(
        (e) => {
          this.setState({
            error: 'Unable to acquire access token for API.',
          });
        }
      );

      if (tokenResponse) {
        this.setState({
          devopsAccessToken: tokenResponse.accessToken,
        });
        sessionStorage.setItem(UATConstants.devopsTokenExpiryTime, tokenResponse.expiresOn);
        sessionStorage.setItem(UATConstants.devopsTokenForADOApi, tokenResponse.accessToken);
      }
    }

    async readMail(accessToken) {
      const emailMessages = await fetchMsGraph(GRAPH_ENDPOINTS.MAIL, accessToken).catch(() => {
        this.setState({
          error: 'Unable to fetch email messages.',
        });
      });
      if (emailMessages) {
        this.setState({
          emailMessages,
          error: null,
        });
      }
    }

    async getProfilePhoto(accessToken) {
      const profilePic = await fetchMsGraphProfilePic(GRAPH_ENDPOINTS.PHOTO, accessToken).catch(
        (error) => {
          console.log(error);
          this.setState({
            error: 'Unable to fetch profile photo.',
          });
        }
      );

      if (profilePic) {
        this.setState({
          profilePic,
          error: null,
        });
      }
    }

    render() {
      return (
        <C
          {...this.props}
          account={this.state.account}
          emailMessages={this.state.emailMessages}
          error={this.state.error}
          graphProfile={this.state.graphProfile}
          profilePic={this.state.profilePic}
          graphAccessToken={this.state.graphAccessToken}
          apiAccessToken={this.state.apiAccessToken}
          coreApiAccessToken={this.state.coreApiAccessToken}
          onSignIn={() => this.onSignIn(useRedirectFlow)}
          onSignOut={() => this.onSignOut()}
          onRequestEmailToken={() => this.onRequestEmailToken()}
          onRequestProfilePic={() => this.onRequestProfilePic()}
          getCoreAPIAccessToken={() => this.getCoreAPIAccessToken()}
          getAPIAccessToken={() => this.getAPIAccessToken()}
        />
      );
    }
  };
